import i18next from 'i18next'
import React, { useMemo, useState } from 'react'
import { AsyncState, StandardLocalWidget, Updater, any, fromJSX } from 'widgets-for-react'
import { authOptions } from '../../site/root/rootWidget'
import { DataLayer } from '../../utils/datalayer'
import {
  FormControls,
  FormNotification,
} from '../../utils/forms'
import { getClassName, useDebounce } from '../../utils/utils'
import { Country } from '../country/countryState'
import { countryWidget } from '../country/countryWidget'
import {
  CompanyState,
  companyStateUpdaters,
  companyUpdaters,
} from './companyState'
import { companyTypeSelectorWidget } from '../companyTypeSelector/companyTypeSelectorWidget'
import { CompanyType } from '../companyTypeSelector/companyTypeSelectorState'
import { singleValidatorResultBuilders } from 'validator/validatorExpr'
import { isVATnumber } from 'utils/vat'
import { validateVAT } from 'subscription/newSubscription/newSubscriptionUtils'

export const companyWidget =
  (
    availableCountries: Array<Country>,
    availableCompanyTypes: Array<CompanyType>,
    readonly: boolean,
    countryReadOnly: boolean, // TODO: Remove me (replaced by state.countryFixed)
    suppressHotjar: boolean,
    headerIsWysiwyg?: boolean,
    origin?: 'upgrade',
    showErrorNotification?: AsyncState<boolean>
  ): StandardLocalWidget<CompanyState> =>
  (currentState) => {
    const showVATError =
      showErrorNotification &&
      showErrorNotification.kind == 'loaded' &&
      !showErrorNotification.value;

      const [unvalidatedInput, setUnvalidatedInput] = useState<string>(currentState.input.VAT ?? '');
      const [isVATCorrect, setIsVATCorrect] = useState<boolean>(true);

    const handleChange = async (countryId: number, vatNumber: string, setState: (_: Updater<CompanyState>) => void) => {

      if(currentState.country.input.Id) {
        countryId = availableCountries.find(availableCountry => availableCountry.Id === currentState.country.input.Id)?.Id ?? 0;
      }

      if (countryId === null || countryId === 0 || vatNumber.length === 0) {
        return;
      }

      if (!isVATnumber.test(vatNumber)) {
        setState(companyStateUpdaters.input(companyUpdaters.VAT((_) => '')));
        return setIsVATCorrect(false);
      }

      const isVIESValidated = await validateVAT(countryId, vatNumber);

      if(isVIESValidated.kind === 'result') {
        setIsVATCorrect(isVIESValidated.value);

        if(isVIESValidated.value) {
          setState(companyStateUpdaters.input(companyUpdaters.VAT((_) => vatNumber)));
        }
        else {
          setState(companyStateUpdaters.input(companyUpdaters.VAT((_) => '')));
        }
      }
    }
    
    const debounce = useDebounce(handleChange, 500);

    const validateVATNumber = useMemo( () => debounce,[] );
    
    let isCompanyVatValid = singleValidatorResultBuilders.failed('vat', i18next.t('subscriptionValidators:VATCountry'));

    if (unvalidatedInput.length === 0 || isVATCorrect) {
      isCompanyVatValid = singleValidatorResultBuilders.passed('vat')
    }

    return any([
      currentState.validator
        .widget()
        .map(companyStateUpdaters.validation),
      fromJSX((setState) => (
        <>
          <div
            className={getClassName(authOptions, 'subHeaderClassName', 'mb-3')}
          >
            {headerIsWysiwyg == true ? (
              <>
                <h3>{i18next.t('subscription:CompanyDetails')}</h3>
                <p>{i18next.t('subscription:CompanyDetailsIntro')}</p>
              </>
            ) : (
              <>
                <h2
                  className={getClassName(
                    authOptions,
                    'subTitleClassName',
                    'mb-3'
                  )}
                >
                  {i18next.t('subscription:CompanyDetails')}
                </h2>
                <p
                  className={getClassName(
                    authOptions,
                    'introClassName',
                    'lead'
                  )}
                >
                  {i18next.t('subscription:CompanyDetailsIntro')}
                </p>
              </>
            )}
          </div>

          {/* VAT - Country error notification */}
          <div id="auth__error-box">
            {showVATError && (
              <FormNotification
                options={authOptions}
                message={i18next.t('subscriptionValidators:VATCountry')}
                extraClasses="lf__subheader"
              />
            )}
          </div>

          <FormControls.FormItemInGroup
            key="subscriptionCompanyName"
            type="text"
            controlId="formCompanyName"
            label={i18next.t('subscription:CompanyName')}
            readOnly={readonly}
            value={currentState.input.name || ''}
            required={true}
            onChange={(newValue) =>
              setState(
                companyStateUpdaters.input(
                  companyUpdaters.name((_) => newValue)
                )
              )
            }
            onBlur={() => {
              if (origin == 'upgrade') DataLayer.upgradeFlowStep2('companyName')
            }}
            validation={currentState.validatorResult.path('name')}
            suppressHotjar={suppressHotjar}
          />

          {
            companyTypeSelectorWidget(availableCompanyTypes, true, readonly, suppressHotjar, () => {if (origin == 'upgrade') DataLayer.upgradeFlowStep2('companyType')})(currentState.companyType)
            .map(companyStateUpdaters.companyType)
            .run(setState)
          }
          
          {/*currentState.lookupData.kind == "loaded" ?
              companyTypeSelectorWidget(
                currentState.lookupData.value.Countries, 
                false,
                readonly,
                true
              )(currentState.editing.companyState)
                .map(subscriptionUpdaters.changePersonalInfoCompanyState)
                .run(setState)
              : <RootLayout.Loader showText hasWrapper />
            */
          }
          
          {/* <FormControls.FormItemInGroup
            key="subscriptionCompanyType"
            type="text"
            controlId="formCompanyType"
            label={i18next.t('subscription:CompanyType')}
            readOnly={readonly}
            value={currentState.input.type || ''}
            required={false}
            onChange={(newValue) =>
              setState(
                companyStateUpdaters.input(
                  companyUpdaters.type((_) => newValue)
                )
              )
            }
            onBlur={() => {
              if (origin == 'upgrade') DataLayer.upgradeFlowStep2('companyType')
            }}
            validation={currentState.validatorResult.path('type')}
            suppressHotjar={suppressHotjar}
          /> */}

          {countryWidget(
            availableCountries,
            suppressHotjar,
            readonly || currentState.countryFixed,
            currentState.countryFixed
              ? i18next.t('subscription:ChangeCountryText')
              : undefined,
            undefined,
            undefined,
            (countryId: number) => {
              validateVATNumber(countryId, unvalidatedInput.trim(), setState);
            }
          )(currentState.country)
            .map(companyStateUpdaters.country)
            .run(setState)}

          <FormControls.FormItemInGroup
            key="subscriptionCompanyVAT"
            type="text"
            controlId="formBasicCompanyVAT"
            label={i18next.t('subscription:CompanyVAT')}
            required={true}
            readOnly={readonly}
            value={unvalidatedInput}
            onChange={(newValue) => {
              setUnvalidatedInput(newValue);
              validateVATNumber(currentState.country.input.Id || 0, newValue.trim(), setState);
            }}
            onBlur={() => {
              if (origin == 'upgrade') DataLayer.upgradeFlowStep2('companyVAT')
            }}
            validation={isCompanyVatValid}
            suppressHotjar={suppressHotjar}
          />
          <FormControls.FormItemInGroup
            key="subscriptionCompanyAddress"
            type="text"
            controlId="formBasicCompanyStreet"
            label={i18next.t('subscription:CompanyAddress')}
            required={true}
            readOnly={readonly}
            value={currentState.input.address || ''}
            onChange={(newValue) =>
              setState(
                companyStateUpdaters.input(
                  companyUpdaters.address((_) => newValue)
                )
              )
            }
            onBlur={() => {
              if (origin == 'upgrade')
                DataLayer.upgradeFlowStep2('companyAddress')
            }}
            validation={currentState.validatorResult.path('address')}
            suppressHotjar={suppressHotjar}
          />
          <FormControls.FormItemInGroup
            key="subscriptionCompanyPostCode"
            type="text"
            controlId="formBasicCompanyPostCode"
            label={i18next.t('subscription:CompanyPostCode')}
            readOnly={readonly}
            required={true}
            value={currentState.input.postCode || ''}
            onChange={(newValue) =>
              setState(
                companyStateUpdaters.input(
                  companyUpdaters.postCode((_) => newValue)
                )
              )
            }
            onBlur={() => {
              if (origin == 'upgrade')
                DataLayer.upgradeFlowStep2('companyPostCode')
            }}
            validation={currentState.validatorResult.path('postCode')}
            suppressHotjar={suppressHotjar}
          />
          <FormControls.FormItemInGroup
            key="subscriptionCompanyCity"
            type="text"
            controlId="formBasicCompanyCity"
            label={i18next.t('subscription:CompanyCity')}
            required={true}
            readOnly={readonly}
            value={currentState.input.city || ''}
            onChange={(newValue) =>
              setState(
                companyStateUpdaters.input(
                  companyUpdaters.city((_) => newValue)
                )
              )
            }
            onBlur={() => {
              if (origin == 'upgrade') DataLayer.upgradeFlowStep2('companyCity')
            }}
            validation={currentState.validatorResult.path('city')}
            suppressHotjar={suppressHotjar}
          />
        </>
      )),
    ])
  }
